/**
 * The five small data-display panels that live inline on the dashboard
 * shell (bridges / matrix / footprint / ledger), plus a typed
 * dispatcher that turns a tab id into a panel. Splitting these out of
 * `App.tsx` keeps the shell focused on routing + chrome and removes the
 * long `if (tab === "...") return <...> else ...` cascade.
 */

import React from "react";
import {
  DemoWorkbenchCard,
  DemoWorkbenchFact,
  DemoWorkbenchFactGrid,
  DemoWorkbenchItemList,
  DemoWorkbenchTable,
} from "@csk/demo-ui";
import { MatrixGraph } from "../components/MatrixGraph";
import { GENERATOR_COLORS } from "../constants";
import type { Bridge, BridgeMatrix, Footprint, Ledger } from "../types";

const AXES = ["G1", "G2", "G3", "G4"] as const;
type Axis = (typeof AXES)[number];

export function BridgesPanel(props: {
  readonly byGenerator: ReadonlyMap<string, readonly Bridge[]>;
}): React.JSX.Element {
  return (
    <div className="grid gap-3 md:grid-cols-4">
      {AXES.map((g) => {
        const list = props.byGenerator.get(g) ?? [];
        const color = GENERATOR_COLORS[g];
        return (
          <DemoWorkbenchCard key={g} title={`${g} (${list.length})`}>
            <DemoWorkbenchItemList
              compact
              items={list.map((bridge) => ({
                id: bridge.bridge_id,
                label: <span className={color.text}>{bridge.bridge_id}</span>,
                detail: bridge.seam ? `(${bridge.seam})` : undefined,
                mono: true,
              }))}
            />
          </DemoWorkbenchCard>
        );
      })}
    </div>
  );
}

export function MatrixPanel(props: {
  readonly matrix: BridgeMatrix;
}): React.JSX.Element {
  return (
    <DemoWorkbenchTable
      headers={["From", "To", "Status", "Shape"]}
      rows={props.matrix.rules.map((r) => [
        r.lhs,
        r.rhs,
        r.status,
        r.reason ?? "—",
      ])}
    />
  );
}

export function GraphPanel(props: {
  readonly matrix: BridgeMatrix;
}): React.JSX.Element {
  return <MatrixGraph matrix={props.matrix} />;
}

export function FootprintPanel(props: {
  readonly footprint: Footprint;
}): React.JSX.Element {
  return (
    <DemoWorkbenchCard title="Governance footprint">
      <DemoWorkbenchFactGrid minColumnWidth={90}>
        {AXES.map((g: Axis) => {
          const color = GENERATOR_COLORS[g];
          return (
            <DemoWorkbenchFact
              key={g}
              label={<span className={color.text}>{g}</span>}
              value={props.footprint.coordinates[g]}
              tone="accent"
            />
          );
        })}
      </DemoWorkbenchFactGrid>
    </DemoWorkbenchCard>
  );
}

export function LedgerPanel(props: {
  readonly ledger: Ledger;
}): React.JSX.Element {
  return (
    <DemoWorkbenchTable
      compact
      headers={["#", "Kind", "File", "Payload", "Hash"]}
      rows={props.ledger.entries.map((e, i) => [
        String(i + 1),
        e.kind,
        e.file,
        e.payload_hash,
        e.hash.slice(0, 12),
      ])}
    />
  );
}
